import React from "react";
import {Box, Accordion, AccordionSummary, AccordionDetails, Typography, Button} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {KpiMainTable} from "./KpiMainTable";
import {KpiWebTable} from "./KpiWebTable";
import {KpiPhoneTable} from "./KpiPhoneTable";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const KpiTablesSection = ({data, loading}) => {
    const formatValue = (value, isPercent = false) => {
        if (value === null || value === undefined) return '0';
        if (isPercent) {
            return parseFloat(value).toFixed(1);
        }
        return value;
    };

    const formatDuration = (value) => {
        if (!value) return '0';
        return value;
    };

    const calculateTotal = (field, dataArray) => {
        return dataArray.reduce((sum, row) => {
            const value = parseFloat(row[field]) || 0;
            return sum + value;
        }, 0);
    };

    const handleExportExcel = () => {
        if (!data || data.length === 0) {
            console.warn('No data to export');
            return;
        }

        const workbook = XLSX.utils.book_new();

        const mainTableHeaders = [
            'KPI User',
            'Outgoing calls',
            'Outgoing calls %',
            'Outgoing calls(>30)',
            'Incoming calls',
            'Incoming calls %',
            'Inbound call duration',
            'Outbound call duration',
            'Total call duration',
            'Web assigned',
            'Converted',
            'Web conversion %',
            'Web to Onsite(Today)',
            'Web to Inside(Today)',
            'Web to Onsite',
            'Web to Inside',
            'Phone to Onsite',
            'Phone to Inside',
            'Outgoing Email',
            'SMS',
            'Number Of Notes'
        ];

        const mainTableData = data.map(row => [
            row.name || '',
            formatValue(row.outbound_calls_count),
            formatValue(row.percentOutboundCalls, true) + '%',
            formatValue(row.outgoing_calls_30_count),
            formatValue(row.inbound_calls_count),
            formatValue(row.percentInboundCalls, true) + '%',
            formatDuration(row.inbound_calls_dur),
            formatDuration(row.outbound_calls_dur),
            formatDuration(row.total_calls_duration),
            formatValue(row.web_assigned),
            formatValue(row.converted),
            formatValue(row.web_percentage, true) + '%',
            formatValue(row.web_outside_today),
            formatValue(row.web_inside_today),
            formatValue(row.web_outside),
            formatValue(row.web_inside),
            formatValue(row.phone_outside),
            formatValue(row.phone_inside),
            formatValue(row.email_count),
            formatValue(row.sms_count),
            formatValue(row.note_count)
        ]);

        const mainTableTotal = [
            'Total',
            calculateTotal('outbound_calls_count', data),
            '',
            '',
            calculateTotal('inbound_calls_count', data),
            '',
            '',
            '',
            '',
            '',
            calculateTotal('converted', data),
            '',
            '',
            '',
            '',
            '',
            calculateTotal('phone_outside', data),
            calculateTotal('phone_inside', data),
            calculateTotal('email_count', data),
            calculateTotal('sms_count', data),
            ''
        ];

        const mainTableSheet = XLSX.utils.aoa_to_sheet([
            mainTableHeaders,
            ...mainTableData,
            mainTableTotal
        ]);
        XLSX.utils.book_append_sheet(workbook, mainTableSheet, 'Web And Phone');

        const webTableHeaders = [
            'KPI User',
            'Web assigned',
            'Converted',
            'Web conversion %',
            'Web to Onsite',
            'Web to Inside',
            'Outgoing Email',
            'Number Of Notes'
        ];

        const webTableData = data.map(row => [
            row.name || '',
            formatValue(row.web_assigned),
            formatValue(row.converted),
            formatValue(row.web_percentage, true) + '%',
            formatValue(row.web_outside),
            formatValue(row.web_inside),
            formatValue(row.email_count),
            formatValue(row.note_count)
        ]);

        const webTableTotal = [
            'Total',
            '',
            '',
            '',
            '',
            '',
            calculateTotal('email_count', data),
            ''
        ];

        const webTableSheet = XLSX.utils.aoa_to_sheet([
            webTableHeaders,
            ...webTableData,
            webTableTotal
        ]);
        XLSX.utils.book_append_sheet(workbook, webTableSheet, 'Web Performance');

        // Sheet 3: Phone Performance
        const phoneTableHeaders = [
            'KPI User',
            'Outgoing calls',
            'Outgoing calls %',
            'Outgoing calls(>30)',
            'Incoming calls',
            'Incoming calls %',
            'Inbound call duration',
            'Outbound call duration',
            'Total call duration',
            'Phone to Onsite',
            'Phone to Inside',
            'SMS'
        ];

        const phoneTableData = data.map(row => [
            row.name || '',
            formatValue(row.outbound_calls_count),
            formatValue(row.percentOutboundCalls, true) + '%',
            formatValue(row.outgoing_calls_30_count),
            formatValue(row.inbound_calls_count),
            formatValue(row.percentInboundCalls, true) + '%',
            formatDuration(row.inbound_calls_dur),
            formatDuration(row.outbound_calls_dur),
            formatDuration(row.total_calls_duration),
            formatValue(row.phone_outside),
            formatValue(row.phone_inside),
            formatValue(row.sms_count)
        ]);

        const phoneTableTotal = [
            'Total',
            calculateTotal('outbound_calls_count', data),
            '',
            '',
            calculateTotal('inbound_calls_count', data),
            '',
            '',
            '',
            '',
            calculateTotal('phone_outside', data),
            calculateTotal('phone_inside', data),
            calculateTotal('sms_count', data)
        ];

        const phoneTableSheet = XLSX.utils.aoa_to_sheet([
            phoneTableHeaders,
            ...phoneTableData,
            phoneTableTotal
        ]);
        XLSX.utils.book_append_sheet(workbook, phoneTableSheet, 'Phone Performance');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `KPI_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        saveAs(blob, fileName);
    };

    return (
        <Box>
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2}}>
                        <Typography variant="h6" fontWeight={700} fontSize="24px" color="#4A5568">
                            Web And Phone
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<FileDownloadIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleExportExcel();
                            }}
                            size="small"
                        >
                            Export to Excel
                        </Button>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <KpiMainTable data={data} loading={loading} />
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" fontWeight={700} fontSize="24px" color="#4A5568">
                        Web Performance
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <KpiWebTable data={data} loading={loading} />
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" fontWeight={700} fontSize="24px" color="#4A5568">
                        Phone Performance
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <KpiPhoneTable data={data} loading={loading} />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};


